import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

export interface ProductionOrderEvent {
  id: string;
  productionOrderId: string;
  customerName: string | null;
  orderNumber: string | null;
  equipmentName: string | null;
  equipmentId: number | null;
  processName: string | null;
  description: string;
  evidenceType: 'TEXT' | 'IMAGE';
  evidenceText: string | null;
  evidenceUrl: string | null;
  occurredAt: string;
  createdBy: number | null;
  createdAt: string;
  createdByName: string | null;
  orderStatus: string | null;
}

export interface CreateEventPayload {
  productionOrderId: string;
  equipmentName?: string | null;
  processName?: string | null;
  description: string;
  evidenceType?: 'TEXT' | 'IMAGE';
  evidenceText?: string | null;
  evidenceUrl?: string | null;
  occurredAt?: string;
}

export function useProductionOrderEvents(orderId: string | undefined) {
  return useQuery<ProductionOrderEvent[]>({
    queryKey: ['production-order-events', orderId],
    queryFn: async () => {
      if (!orderId) return [];
      const response = await axios.get(`/api/production-order-events/order/${orderId}`);
      return response.data;
    },
    enabled: !!orderId,
  });
}

export function useRecentProductionOrderEvents(limit: number = 50) {
  return useQuery<ProductionOrderEvent[]>({
    queryKey: ['production-order-events', 'recent', limit],
    queryFn: async () => {
      const response = await axios.get(`/api/production-order-events/recent?limit=${limit}`);
      return response.data;
    },
  });
}

export function useCreateProductionOrderEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateEventPayload) => {
      const response = await axios.post('/api/production-order-events', payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['production-order-events', variables.productionOrderId] });
      queryClient.invalidateQueries({ queryKey: ['production-order-events', 'recent'] });
    },
  });
}

export function useDeleteProductionOrderEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const response = await axios.delete(`/api/production-order-events/${eventId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-order-events'] });
    },
  });
}

export async function logProductionOrderEvent(payload: CreateEventPayload): Promise<ProductionOrderEvent> {
  const response = await axios.post('/api/production-order-events', payload);
  return response.data;
}

export async function logTextEvent(
  productionOrderId: string,
  description: string,
  options?: {
    equipmentName?: string;
    processName?: string;
    evidenceText?: string;
  }
): Promise<ProductionOrderEvent> {
  return logProductionOrderEvent({
    productionOrderId,
    description,
    evidenceType: 'TEXT',
    evidenceText: options?.evidenceText,
    equipmentName: options?.equipmentName,
    processName: options?.processName,
  });
}

export async function logImageEvent(
  productionOrderId: string,
  description: string,
  imageUrl: string,
  options?: {
    equipmentName?: string;
    processName?: string;
  }
): Promise<ProductionOrderEvent> {
  return logProductionOrderEvent({
    productionOrderId,
    description,
    evidenceType: 'IMAGE',
    evidenceUrl: imageUrl,
    equipmentName: options?.equipmentName,
    processName: options?.processName,
  });
}

export function useProductionOrderEventsSSE(limit: number = 50, enablePolling: boolean = true) {
  const [events, setEvents] = useState<ProductionOrderEvent[]>([]);
  const [newEventIds, setNewEventIds] = useState<Set<string>>(new Set());
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const previousEventIdsRef = useRef<Set<string>>(new Set());
  const isFirstLoadRef = useRef(true);

  const { data: initialEvents, isLoading, refetch } = useQuery<ProductionOrderEvent[]>({
    queryKey: ['production-order-events', 'recent', limit],
    queryFn: async () => {
      const response = await axios.get(`/api/production-order-events/recent?limit=${limit}`);
      return response.data;
    },
    refetchInterval: enablePolling ? 10000 : false,
    staleTime: 5000,
  });

  useEffect(() => {
    if (initialEvents) {
      const currentIds = new Set(initialEvents.map(e => e.id));
      const newIds = new Set<string>();

      currentIds.forEach(id => {
        if (!previousEventIdsRef.current.has(id)) {
          newIds.add(id);
        }
      });

      if (newIds.size > 0 && previousEventIdsRef.current.size > 0 && !isFirstLoadRef.current) {
        setNewEventIds(newIds);
        setTimeout(() => {
          setNewEventIds(new Set());
        }, 2000);
      }

      previousEventIdsRef.current = currentIds;
      setEvents(initialEvents);
      
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        setIsBootstrapping(false);
      }
    }
  }, [initialEvents]);

  return {
    events,
    newEventIds,
    isConnected: enablePolling,
    isLoading: isBootstrapping && isLoading,
    isBootstrapping,
    error: null,
    refetch,
  };
}
